import type { IPlatform, IPlatformApp, IPlatformStorage, IPlatformUpdater, IPlatformWatchdog } from './types'
import { requestWebFullscreen } from '@/helpers/webFullscreen'

const webStorage: IPlatformStorage = {
  async get(key: string) {
    if (typeof window === 'undefined') { return null }
    try {
      return window.localStorage.getItem(key)
    }
    catch {
      return null
    }
  },
  async set(key: string, value: string) {
    if (typeof window === 'undefined') { return }
    try {
      window.localStorage.setItem(key, value)
    }
    catch {
      // ignore
    }
  },
  async remove(key: string) {
    if (typeof window === 'undefined') { return }
    try {
      window.localStorage.removeItem(key)
    }
    catch {
      // ignore
    }
  }
}

const webApp: IPlatformApp = {
  async exitApp() {
    // Web has no exit; caller may close window or navigate
  },
  async getInfo() {
    return null
  },
  onAppStateChange(callback: (isActive: boolean) => void) {
    if (typeof document === 'undefined') { return () => {} }
    const handler = () => callback(document.visibilityState === 'visible')
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }
}

const webUpdater: IPlatformUpdater = {
  async notifyAppReady() {},
  async update() {},
  async reset() {
    window.location.reload()
  }
}

const webWatchdog: IPlatformWatchdog = {
  async start() {},
  async ping() {},
  async stop() {}
}

function randomUuid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const WEB_DEVICE_ID_KEY = 'LUCIMARK:WEB:DEVICE_ID'

/** Stable device id for web: random UUID persisted in localStorage. */
async function webDeviceId(): Promise<string> {
  let id = await webStorage.get(WEB_DEVICE_ID_KEY)
  if (!id) {
    id = randomUuid()
    await webStorage.set(WEB_DEVICE_ID_KEY, id)
  }
  return id
}

async function webRequestFullscreen(): Promise<void> {
  await requestWebFullscreen()
}

const webPlatform: IPlatform = {
  storage: webStorage,
  app: webApp,
  updater: webUpdater,
  watchdog: webWatchdog,
  deviceId: webDeviceId,
  async keepAwake() {
    // No-op on web
  },
  async openSystemSettings() {
    // No-op on web
  },
  requestFullscreen: webRequestFullscreen
}

export { webPlatform }
